import * as t from 'io-ts';

import {makeApiSerializableType, makeApiType, RInboxIdRef} from './apiTypes';
import {RBusinessHours} from './businessHoursModel';

/*
 * Runtime type.
 */

const RInboxBase = t.type({
  id: t.Integer,
  namespace: t.string,
  alias: t.string,
  name: t.string,
  description: t.string,
  teammateIds: t.readonlyArray(t.Integer),
  isPublic: t.boolean,
  color: t.string,
  updatedAt: t.Integer,
  url: t.string,
  isAutojoinOn: t.boolean,
  isAnalyticsVisible: t.boolean,
  businessHours: t.union([RBusinessHours, t.undefined, t.null]),
  areGlobalRulesEnabled: t.boolean,
  inactivityTimeout: t.Integer,
  unassignInactive: t.boolean,
  isSplit: t.boolean,
  numMoveBundles: t.number,
});

const RInboxDirty = t.readonly(RInboxBase, 'inbox');

/*
 * Type.
 */

export interface InboxModel extends t.TypeOf<typeof RInboxDirty> {}
export const RInbox = t.clean<InboxModel>(RInboxDirty as any);
RInboxIdRef.setTargetType(RInbox);
const RInboxes = t.readonlyArray(RInbox);

/*
 * IO methods.
 */

export function parseInbox(src: any): InboxModel {
  const teammateIds = src.teammate_ids;
  const inboxOwnerId = src.namespace.startsWith('tea:') && Number(src.namespace.slice(4));
  if (Number.isInteger(inboxOwnerId) && !teammateIds.includes(inboxOwnerId)) {
    teammateIds.push(inboxOwnerId);
  }

  return {
    id: src.id,
    namespace: src.namespace,
    alias: src.alias,
    name: src.name,
    description: src.description || '',
    teammateIds,
    isPublic: Boolean(src.is_public),
    color: src.color,
    updatedAt: src.updated_at,
    url: src.url,
    isAutojoinOn: Boolean(src.settings && src.settings.autojoin),
    isAnalyticsVisible: Boolean(src.settings && src.settings.analyticsVisible),
    businessHours: src.settings?.businessHours,
    areGlobalRulesEnabled: Boolean(src.settings?.globalRulesEnabled),
    unassignInactive: Boolean(src.settings?.unassignInactive),
    inactivityTimeout: src.settings?.inactivityTimeout ?? 432000000,
    isSplit: src.threadingNamespaceBehavior === 'split',
    numMoveBundles: src.num_move_bundles ?? 0,
  };
}

export function parseInboxes(src: any) {
  return src.inboxes && src.inboxes.map(parseInbox);
}

export function serializeInbox(inbox: InboxModel): any {
  return {
    name: inbox.name,
    description: inbox.description,
    namespace: inbox.namespace,
    teammate_ids: inbox.teammateIds,
    is_public: inbox.isPublic,
    color: inbox.color,
    threading_namespace_behavior: inbox.isSplit ? 'split' : 'unsplit',
    settings: {
      autojoin: inbox.isAutojoinOn,
      analyticsVisible: inbox.isAnalyticsVisible,
      businessHours: inbox.businessHours,
      globalRulesEnabled: inbox.areGlobalRulesEnabled,
      inactivityTimeout: inbox.inactivityTimeout,
      unassignInactive: inbox.unassignInactive,
    },
  };
}

/*
 * API type.
 */

export const ApiInbox = makeApiSerializableType('inbox', RInbox, parseInbox, serializeInbox);
export const ApiInboxes = makeApiType('inboxes', RInboxes, parseInboxes);
